import { useState, useEffect } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, DollarSign, Sparkles, Upload, CheckCircle2, Clock, XCircle, Edit2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const CATEGORIES = [
  "BRAKES", "TIRES", "ENGINE", "TRANSMISSION", "ELECTRICAL", "HVAC",
  "SUSPENSION", "EXHAUST", "COOLING", "FUEL", "AIR_SYSTEM", "LIGHTS",
  "TOWING", "ROADSIDE", "PM_SERVICE", "DOT_INSPECTION", "TRAILER",
  "REEFER", "GLASS", "BODY", "LABOR", "OTHER",
];
const UNITS = ["each", "hour", "set", "service", "axle", "per_tire", "flat", "mile", "trip", "call"];

const STATUS_BADGES = {
  APPROVED: { label: 'Live', cls: 'bg-green-900/30 text-green-400 border-green-800/50', icon: CheckCircle2 },
  PENDING: { label: 'Awaiting Ops', cls: 'bg-amber-900/30 text-amber-400 border-amber-800/50', icon: Clock },
  REJECTED: { label: 'Rejected', cls: 'bg-red-900/30 text-red-400 border-red-800/50', icon: XCircle },
};

export default function VendorMyPricing() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ category: 'BRAKES', service_code: '', description: '', unit_price: '', unit: 'each', notes: '' });
  const [saving, setSaving] = useState(false);
  const [aiUploading, setAiUploading] = useState(false);

  useEffect(() => { boot(); }, []);

  const boot = async () => {
    setLoading(true);
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      if (meRes.data.vendor_id) {
        const r = await api.get(`/vendor-pricing/vendors/${meRes.data.vendor_id}/pricing`, { params: { status: 'ALL' } });
        setItems(r.data.items || []);
      }
    } catch (e) {
      toast.error('Failed to load pricing');
    } finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/vendor-pricing/my-shop/pricing/propose', {
        ...form,
        unit_price: parseFloat(form.unit_price) || 0,
      });
      toast.success('Submitted — awaiting ops approval');
      setShowAdd(false);
      setForm({ category: 'BRAKES', service_code: '', description: '', unit_price: '', unit: 'each', notes: '' });
      boot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setSaving(true);
    try {
      await api.patch(`/vendor-pricing/my-shop/pricing/items/${editTarget.id}`, {
        ...form,
        unit_price: parseFloat(form.unit_price) || 0,
      });
      toast.success(editTarget.approval_status === 'PENDING' ? 'Updated' : 'Edit submitted — awaiting ops review');
      setEditTarget(null);
      boot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(item.approval_status === 'PENDING'
      ? 'Delete this proposal?'
      : 'Request deletion of this live price? Ops will need to approve.')) return;
    try {
      await api.delete(`/vendor-pricing/my-shop/pricing/items/${item.id}`);
      toast.success(item.approval_status === 'PENDING' ? 'Deleted' : 'Deletion submitted for ops review');
      boot();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAiUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !me?.vendor_id) return;
    setAiUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post(`/vendor-pricing/vendors/${me.vendor_id}/pricing/import/ai-extract`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const rows = res.data.preview || [];
      toast.success(`AI extracted ${rows.length} rows — submitting as proposals`);
      // Submit each row as a vendor proposal
      let ok = 0;
      for (const r of rows) {
        try {
          await api.post('/vendor-pricing/my-shop/pricing/propose', r);
          ok += 1;
        } catch { /* skip individual failures */ }
      }
      toast.success(`${ok} of ${rows.length} priced rows submitted for ops review`);
      boot();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI extraction failed');
    } finally {
      setAiUploading(false);
      e.target.value = '';
    }
  };

  const startEdit = (item) => {
    const live = item.proposed_change || item;
    setForm({
      category: live.category || item.category,
      service_code: live.service_code || item.service_code || '',
      description: live.description || item.description || '',
      unit_price: String(live.unit_price ?? item.unit_price ?? ''),
      unit: live.unit || item.unit || 'each',
      notes: live.notes || item.notes || '',
    });
    setEditTarget(item);
  };

  const pendingCount = items.filter(i => i.approval_status === 'PENDING' || i.proposed_change).length;
  const liveCount = items.filter(i => i.approval_status === 'APPROVED' && !i.proposed_change).length;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="vendor-my-pricing">
      <div className="pb-4 border-b border-[#2D3F5E]/60 flex items-center justify-between">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// VENDOR PORTAL</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl flex items-center gap-2">
            <DollarSign size={22} className="text-[#3B82F6]" /> My Pricing
          </h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mt-1">
            {liveCount} live · {pendingCount} awaiting ops approval
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wide font-['JetBrains_Mono'] border cursor-pointer inline-flex items-center gap-1.5 ${aiUploading ? 'opacity-60 pointer-events-none' : ''}`}
            style={{ background: 'rgba(147, 51, 234, 0.10)', borderColor: 'rgba(147, 51, 234, 0.5)', color: '#C084FC' }}
            data-testid="vendor-ai-upload-label">
            {aiUploading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {aiUploading ? 'Extracting…' : 'AI Upload'}
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleAiUpload}
              disabled={aiUploading} data-testid="vendor-ai-upload-input" />
          </label>
          <button onClick={() => setShowAdd(true)} data-testid="vendor-add-price-btn"
            className="px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wide bg-[#3B82F6] text-white hover:bg-[#2563EB] inline-flex items-center gap-1.5">
            <Plus size={12} /> Add Price
          </button>
        </div>
      </div>

      {!me?.vendor_id && !loading && (
        <div className="p-3 rounded-sm border flex items-start gap-2" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }}>
          <div className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--warning)' }}>
            Your account isn't linked to a shop yet. Ask the ops admin to attach a vendor on your profile before adding prices.
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#3B82F6]" size={20} /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center card-industrial">
          <DollarSign size={32} className="mx-auto mb-3 text-[#2D3F5E]" />
          <p className="text-[#5C6A7A] font-['IBM_Plex_Sans']">No pricing submitted yet</p>
          <p className="text-[10px] mt-2 font-['JetBrains_Mono'] text-[#5C6A7A]">
            Add prices manually or upload your rate-card PDF — ops will review before they go live.
          </p>
        </div>
      ) : (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['STATUS','CATEGORY','CODE','DESCRIPTION','PRICE','UNIT','ACTIONS'].map((h,i) => (
                  <th key={`hdr-${i}`} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const status = item.proposed_change ? 'PENDING_EDIT' : item.approval_status;
                const badge = STATUS_BADGES[item.approval_status] || STATUS_BADGES.APPROVED;
                const Icon = badge.icon;
                const live = item.proposed_change || {};
                return (
                  <tr key={item.id} className="border-b border-[#2D3F5E]/30 hover:bg-[#1A2332]" data-testid={`pricing-row-${item.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border font-['JetBrains_Mono'] ${badge.cls}`}>
                        <Icon size={10} />
                        {item.proposed_change ? 'Edit Pending' : badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#B8C0CC] font-['JetBrains_Mono']">{item.category}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{item.service_code || '—'}</td>
                    <td className="px-4 py-3 text-sm text-white font-['IBM_Plex_Sans']">
                      {item.description}
                      {item.proposed_change && live.description && live.description !== item.description && (
                        <div className="text-[10px] text-amber-400 mt-0.5 font-['JetBrains_Mono']">→ proposed: {live.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#57EB31] font-['JetBrains_Mono'] font-bold">
                      ${item.unit_price?.toFixed(2)}
                      {item.proposed_change && live.unit_price !== undefined && live.unit_price !== item.unit_price && (
                        <div className="text-[10px] text-amber-400 mt-0.5">→ ${Number(live.unit_price).toFixed(2)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">/{item.unit}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(item)} className="p-1.5 rounded hover:bg-[#0A0F1A]" data-testid={`edit-${item.id}`}>
                          <Edit2 size={12} className="text-[#8B9AAB]" />
                        </button>
                        <button onClick={() => handleDelete(item)} className="p-1.5 rounded hover:bg-red-900/30" data-testid={`delete-${item.id}`}>
                          <Trash2 size={12} className="text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      <Dialog open={showAdd || !!editTarget} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditTarget(null); }}}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope']">
              {editTarget ? 'Edit Price' : 'Propose New Price'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editTarget ? handleEdit : handleAdd} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Category *</label>
                <select required value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}
                  className="w-full h-9 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Service Code</label>
                <input value={form.service_code} onChange={e => setForm(p => ({...p, service_code: e.target.value}))}
                  className="w-full h-9 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Description *</label>
              <input required value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
                className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Unit Price ($) *</label>
                <input required type="number" step="0.01" value={form.unit_price} onChange={e => setForm(p => ({...p, unit_price: e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Unit</label>
                <select value={form.unit} onChange={e => setForm(p => ({...p, unit: e.target.value}))}
                  className="w-full h-9 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]">
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
                className="w-full px-3 py-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6] resize-none" />
            </div>
            <p className="text-[10px] font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>
              {editTarget?.approval_status === 'PENDING'
                ? 'This proposal is still pending — your edit will replace it directly.'
                : 'Ops will review before this price is used in vendor matching.'}
            </p>
            <button type="submit" disabled={saving} data-testid="vendor-pricing-submit"
              className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {editTarget ? 'Submit Edit' : 'Submit Proposal'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
